import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Typography, Dialog } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import CreditCardIcon from '@mui/icons-material/CreditCardOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlineOutlined'
import { useMutation } from '@apollo/client'
import { colors } from '../../const/colors'
import { IAccessCard } from '../../graphql/users/queryUser'
import { IAccessCardFormat } from '../../graphql/users/queryAccessCardFormat'
import {
  REMOVE_ACCESS_CARD_FOR_USER,
  IRemoveAccessCardForUserData,
  IRemoveAccessCardForUserVars,
} from '../../graphql/users/removeAccessCardForUser'
import AccessCardTypeForm from '../AccessCard/AccessCardTypeForm'
import AccessCardNumberForm from '../AccessCard/AccessCardNumberForm'
import BaseModal from '../Shared/BaseModal'
import { SectionButton } from '../Shared/SectionButton'
import { useSnackbar } from '../Shared/useSnackbar'
import { CARD_SX, CloseCircle, SectionHeader } from './UserDetailsUi'

const AccessCardSection: FC<{
  login: string
  accessCard?: IAccessCard
  assigned: boolean
  refetch: () => void
  step?: number
  stepDone?: boolean
  onSaved?: () => void
}> = ({ login, accessCard, assigned, refetch, step, stepDone, onSaved }) => {
  const { t } = useTranslation(['user_details', 'tenant', 'btn'])
  const { notifySuccess, notifyError } = useSnackbar()
  const [pickerOpen, setPickerOpen] = useState(false)
  const [chosenFormat, setChosenFormat] = useState<IAccessCardFormat>()
  const [removeOpen, setRemoveOpen] = useState(false)

  const hasCard = !!accessCard?.number
  const activeFormat = chosenFormat ?? accessCard?.format
  const missingFormat = hasCard && !accessCard?.format
  const keepsNumber =
    hasCard && (activeFormat?.type ?? accessCard?.type) === accessCard?.type

  const [removeAccessCard] = useMutation<
    IRemoveAccessCardForUserData,
    IRemoveAccessCardForUserVars
  >(REMOVE_ACCESS_CARD_FOR_USER, { onError: notifyError })

  const handleRemove = async () => {
    if (!hasCard) return
    const res = await removeAccessCard({ variables: { login } })
    setRemoveOpen(false)
    const payload = res.data?.removeAccessCardForUser
    if (!payload?.status) {
      const reason = payload?.message || payload?.error
      if (reason) notifyError(reason)
      return
    }
    notifySuccess()
    refetch()
  }

  const pickFormat = (format: IAccessCardFormat) => {
    setChosenFormat(format)
    setPickerOpen(false)
  }

  return (
    <Box sx={CARD_SX}>
      <SectionHeader
        icon={<CreditCardIcon sx={{ fontSize: 18 }} />}
        title={t('access_card')}
        step={step}
        done={stepDone}
        action={
          !!activeFormat && (
            <SectionButton
              label={t('change_card_type')}
              icon={<EditOutlinedIcon sx={{ fontSize: 17 }} />}
              tone="neutral"
              onClick={() => setPickerOpen(true)}
            />
          )
        }
      />

      {activeFormat || hasCard ? (
        <AccessCardNumberForm
          key={activeFormat?.name}
          login={login}
          cardFormat={activeFormat}
          formatLabel={
            !activeFormat && (
              <Box sx={{ alignSelf: 'flex-start' }}>
                <SectionButton
                  label={t('add_card_format')}
                  e2e="add-card-format"
                  icon={<AddIcon sx={{ fontSize: 17 }} />}
                  height={36}
                  width="auto"
                  onClick={() => setPickerOpen(true)}
                />
              </Box>
            )
          }
          accessCard={keepsNumber ? accessCard : undefined}
          refetch={refetch}
          callback={() => {
            setChosenFormat(undefined)
            onSaved?.()
          }}
          extraActions={
            hasCard && (
              <SectionButton
                label={t('remove_card')}
                e2e="remove-access-card"
                icon={<DeleteOutlineIcon sx={{ fontSize: 17 }} />}
                tone="danger"
                disabled={assigned}
                onClick={() => setRemoveOpen(true)}
              />
            )
          }
        />
      ) : (
        <Box
          onClick={() => setPickerOpen(true)}
          data-e2e="add-access-card"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '9px',
            height: 54,
            border: `1.5px dashed ${colors.borderDash}`,
            borderRadius: '13px',
            color: colors.navy,
            fontWeight: 700,
            fontSize: '14px',
            cursor: 'pointer',
            transition: 'border-color .15s, background .15s',
            '&:hover': { borderColor: colors.navy, background: '#F7FAFB' },
          }}
        >
          <AddIcon sx={{ fontSize: 18 }} />
          {t('add_card')}
        </Box>
      )}

      <Dialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: colors.radiusModal,
            width: 520,
            maxWidth: '100%',
            p: '28px',
            m: 2,
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: '12px',
            mb: '6px',
          }}
        >
          <Typography
            sx={{ fontSize: '20px', fontWeight: 800, color: colors.navy }}
          >
            {t('access_card')}
          </Typography>
          <CloseCircle onClick={() => setPickerOpen(false)} size={32} />
        </Box>
        <Typography
          sx={{ fontSize: '13.5px', color: colors.textMuted, mb: '20px' }}
        >
          {t('chose_card_type')}
        </Typography>
        {pickerOpen && (
          <AccessCardTypeForm
            setChosenFormat={pickFormat}
            type={missingFormat ? accessCard?.type : undefined}
            currentFormat={activeFormat?.name}
          />
        )}
      </Dialog>

      <BaseModal
        open={removeOpen}
        confirmText={t('btn:confirm')}
        handleClose={() => setRemoveOpen(false)}
        handleAccept={handleRemove}
      >
        <Box sx={{ p: '16px', maxWidth: 420 }}>
          <Typography sx={{ mb: '12px' }}>
            {t('remove_card_confirmation')}
          </Typography>
          <Typography sx={{ color: colors.red, fontSize: '14px' }}>
            {t('parking_pool:unassign_card_warning')}
          </Typography>
        </Box>
      </BaseModal>
    </Box>
  )
}

export default AccessCardSection
